import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    forwardRef,
} from "react";
import { twMerge } from "tailwind-merge";
import { UserEntry, BotEntry, FollowUp } from "../common/chat-entries";
import CommunicationOptions from "../common/communication-options";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { ArrowUp } from "lucide-react";

const CustomSmall = ({ children }) => (
    <div className="border-t border-gray-300 mt-1 leading-3">
        <span style={{ fontSize: "0.75em", color: "gray" }}>{children}</span>
    </div>
);

const CustomLink = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-500 hover:text-sky-600 underline   "
    >
        {children}
    </a>
);

const DEFAULT_PRIVACY_POLICY =
    "By using this chat, you agree to our [privacy policy](https://chattrick-app.witniumtech.com/privacy-policy)";

const Chat = forwardRef(
    (
        {
            // Core functionality props
            apiBaseUrl,
            botId,
            initialMessage = "",
            initialMessageIsSilent = false,
            chatContext = [],
            onSocketStateChange,

            // Chat container styling
            containerClassName = "h-full flex flex-col space-y-4",
            chatContainerClassName = "flex-1 overflow-y-auto border border-neutral-200 rounded-lg bg-white",
            messageContainerClassName = "min-h-full space-y-4 text-sm",

            // Input area styling
            inputContainerClassName = "mt-auto space-y-4",
            inputClassName = "bg-white block w-full rounded-l-md border-0 py-1.5 px-4 text-black text-xs sm:text-sm bg-transparent ring-1 ring-inset ring-neutral-200 placeholder:text-black/60 focus:ring-1 focus:ring-inset focus:outline-none",
            sendButtonClassName = "relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md p-2 text-xs sm:text-sm font-semibold text-sky-500 ring-1 ring-inset ring-neutral-200 hover:text-sky-400",

            // Message rendering (optional overrides)
            renderUserMessage,
            renderBotMessage,
            renderContactOptions,
            renderFollowUps,
            showPrivacyBanner = true,

            // Customization options
            customDisclaimerText,

            // Additional features
            persistConnection = false,

            // Render props (completely customized UI)
            renderInput,
            renderContainer,
            privacyPolicy,
        },
        ref
    ) => {
        const [messages, setMessages] = useState([]);
        const [input, setInput] = useState("");
        const [response, setResponse] = useState("");
        const [followUps, setFollowUps] = useState([]);
        const [chatState, setChatState] = useState({
            connected: false,
            ready: false,
            busy: false,
        });
        const firstBotMessageRef = useRef(true);
        const [showContactOptions, setShowContactOptions] = useState(false);
        const [contactChannels, setContactChannels] = useState({});

        const responseRef = useRef("");
        const chatContainerRef = useRef(null);
        const specialBufferRef = useRef("");
        const initialMessageSentRef = useRef(false);
        const inputRef = useRef(null);
        const ignoreNextResponseRef = useRef(false);
        const referrerRef = useRef(null);
        const referrer = window.location;
        referrerRef.current = referrer;

        // SessionID to track contact options
        const sessionIdRef = useRef(`session-${Date.now()}`);

        // Notify parent component of chat state changes (renamed from socketState)
        useEffect(() => {
            if (onSocketStateChange) {
                onSocketStateChange(chatState);
            }
        }, [chatState, onSocketStateChange]);

        // Initialize chat on mount - simplified
        useEffect(() => {
            setChatState((prev) => ({ ...prev, connected: true, ready: true }));
            return () => {}; // No cleanup needed
        }, []);

        // Focus input field when AI finishes responding (busy changes from true to false)
        useEffect(() => {
            if (inputRef.current && !chatState.busy) {
                // Small delay to ensure the DOM is fully updated
                setTimeout(() => {
                    inputRef.current.focus();
                }, 100);
            }
        }, [chatState.busy]);

        // Scroll to bottom of chat when messages change
        useEffect(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop =
                    chatContainerRef.current.scrollHeight;
            }
        }, [messages, response]);

        // Main function to send chat messages using fetch streaming
        const sendChatMessage = useCallback(
            async (message) => {
                // Add validation to prevent empty messages
                if (!message || !message.trim()) {
                    console.warn("Attempted to send empty message");
                    return;
                }

                if (!chatState.busy) {
                    console.log("Sending chat message:", message);
                    ignoreNextResponseRef.current = false;
                    setChatState((prev) => ({ ...prev, busy: true }));

                    const filteredMessages = messages
                        .filter((msg) => msg.type !== "contact-options")
                        .concat([{ type: 0, message }]);

                    try {
                        const response = await fetch(
                            `${apiBaseUrl}/api/chat-stream`,
                            {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    "X-Session-ID": sessionIdRef.current,
                                },
                                body: JSON.stringify({
                                    messages: filteredMessages,
                                    botId,
                                    chatContext,
                                    referrer: referrerRef.current.origin,
                                }),
                            }
                        );

                        if (!response.ok) {
                            throw new Error(`Server error: ${response.status}`);
                        }

                        const reader = response.body.getReader();
                        const decoder = new TextDecoder();

                        // Reset response buffer at the start of a new message
                        responseRef.current = "";
                        setResponse("");

                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;

                            const text = decoder.decode(value);
                            const lines = text.split("\n\n");

                            for (const line of lines) {
                                if (!line.startsWith("data:")) continue;

                                try {
                                    const trimmedData = line.slice(5).trim();

                                    // Handle special [DONE] marker directly
                                    if (trimmedData === "[DONE]") {
                                        // Stream complete
                                        break;
                                    }

                                    const data = JSON.parse(trimmedData);

                                    // Handle different event types
                                    switch (data.type) {
                                        case "ready":
                                            // Session is ready
                                            break;

                                        case "chat-response-progress":
                                            // Handle character-by-character typing effect
                                            if (
                                                !ignoreNextResponseRef.current
                                            ) {
                                                responseRef.current +=
                                                    data.data;
                                                setResponse(
                                                    (prev) => prev + data.data
                                                );
                                            }
                                            break;

                                        case "chat-response-finished":
                                            // Handle completed response
                                            if (
                                                !ignoreNextResponseRef.current
                                            ) {
                                                const newMessage = {
                                                    type: 1,
                                                    message:
                                                        responseRef.current,
                                                    isFirstBotMessage:
                                                        firstBotMessageRef.current,
                                                };
                                                setMessages((prev) => [
                                                    ...prev,
                                                    newMessage,
                                                ]);
                                                if (
                                                    firstBotMessageRef.current
                                                ) {
                                                    firstBotMessageRef.current = false;
                                                }
                                                setResponse("");
                                                setChatState((prev) => ({
                                                    ...prev,
                                                    busy: false,
                                                }));

                                                // Focus input field after response is complete
                                                setTimeout(() => {
                                                    if (inputRef.current) {
                                                        inputRef.current.focus();
                                                    }
                                                }, 100);

                                                // Try focusing again after a slight delay for better reliability
                                                setTimeout(() => {
                                                    if (inputRef.current) {
                                                        inputRef.current.focus();
                                                    }
                                                }, 300);
                                            }
                                            break;

                                        case "contact-options":
                                            // Handle contact options similar to how socket.io did it
                                            console.log(
                                                "contact-options",
                                                data
                                            );
                                            setMessages((prev) => [
                                                ...prev,
                                                {
                                                    type: "contact-options",
                                                    contactOptions: {
                                                        channels:
                                                            data.channels || {},
                                                        header:
                                                            data.header ||
                                                            "Contact Us",
                                                        message:
                                                            data.message ||
                                                            "Choose how you'd like to contact us:",
                                                    },
                                                },
                                            ]);
                                            responseRef.current = "";
                                            setResponse("");
                                            setChatState((prev) => ({
                                                ...prev,
                                                busy: false,
                                            }));
                                            setContactChannels(
                                                data.channels || {}
                                            );
                                            setShowContactOptions(true);
                                            ignoreNextResponseRef.current = true;
                                            break;

                                        case "error":
                                            // Handle errors
                                            console.error(
                                                "Error from server:",
                                                data.message
                                            );
                                            setResponse("");
                                            setChatState((prev) => ({
                                                ...prev,
                                                busy: false,
                                            }));
                                            break;

                                        default:
                                            console.warn(
                                                "Unknown event type:",
                                                data.type
                                            );
                                    }
                                } catch (e) {
                                    console.error(
                                        "Error parsing SSE data:",
                                        e,
                                        line
                                    );
                                }
                            }
                        }
                    } catch (error) {
                        console.error("Error sending chat message:", error);
                        setChatState((prev) => ({ ...prev, busy: false }));
                    }

                    setFollowUps([]);
                }
            },
            [chatState.busy, messages, chatContext, apiBaseUrl, botId]
        );

        useEffect(() => {
            if (
                chatState.ready &&
                initialMessage &&
                !initialMessageSentRef.current
            ) {
                initialMessageSentRef.current = true;
                sendChatMessage(initialMessage);
                if (!initialMessageIsSilent) {
                    setMessages((prev) => [
                        ...prev,
                        { type: 0, message: initialMessage },
                    ]);
                }
            }
        }, [
            chatState.ready,
            initialMessage,
            initialMessageIsSilent,
            sendChatMessage,
        ]);

        // Create a function to handle sending input messages
        const sendInputMessage = useCallback(() => {
            if (input.trim()) {
                setMessages((prev) => [...prev, { type: 0, message: input }]);
                sendChatMessage(input);
                setInput("");
            }
        }, [input, sendChatMessage]);

        // Expose methods via ref
        React.useImperativeHandle(ref, () => ({
            sendMessage: (message, addToHistory = true) => {
                if (message.trim()) {
                    if (addToHistory) {
                        setMessages((prev) => [...prev, { type: 0, message }]);
                    }
                    sendChatMessage(message);
                }
            },
            getChatState: () => chatState,
            getMessages: () => messages,
            clearMessages: () => setMessages([]),
            setCustomInput: (text) => setInput(text),
        }));

        // Default message renderers that can be overridden by props
        const defaultRenderUserMessage = (msg, index) => (
            <UserEntry key={index} entry={msg} />
        );

        const defaultRenderBotMessage = (msg, index) => (
            <BotEntry
                key={index}
                entry={msg}
                isFirstBotMessage={msg.isFirstBotMessage}
                customDisclaimerText={customDisclaimerText}
            />
        );

        const defaultRenderContactOptions = (msg, index) => {
            const options = msg.contactOptions || {};
            return (
                <CommunicationOptions
                    key={index}
                    channels={options.channels || {}}
                    headerText={options.header || "Contact Us"}
                    subText={
                        options.message ||
                        "Choose how you'd like to contact us:"
                    }
                />
            );
        };

        const defaultRenderFollowUps = (followUps) =>
            followUps.map((question, index) => (
                <FollowUp
                    key={index}
                    question={question}
                    onSendFollowUp={sendChatMessage}
                />
            ));

        // Use provided renderers or defaults
        const renderMessageFunctions = {
            userMessage: renderUserMessage || defaultRenderUserMessage,
            botMessage: renderBotMessage || defaultRenderBotMessage,
            contactOptions: renderContactOptions || defaultRenderContactOptions,
            followUps: renderFollowUps || defaultRenderFollowUps,
        };

        // Render all messages with appropriate renderer
        const messageElements = useMemo(
            () =>
                messages.map((msg, index) => {
                    if (msg.type === 0) {
                        return renderMessageFunctions.userMessage(msg, index);
                    } else if (msg.type === 1 || msg.type === 2) {
                        return renderMessageFunctions.botMessage(msg, index);
                    } else if (msg.type === "contact-options") {
                        return renderMessageFunctions.contactOptions(
                            msg,
                            index
                        );
                    }
                    return null;
                }),
            [messages, renderMessageFunctions]
        );

        // Render follow-ups
        const followUpElements = useMemo(
            () => renderMessageFunctions.followUps(followUps),
            [followUps, renderMessageFunctions]
        );

        // Update the renderInputField function to use sendInputMessage
        const renderInputField = () => {
            if (renderInput) {
                return renderInput({
                    value: input,
                    onChange: (e) => setInput(e.target.value),
                    onSend: sendInputMessage, // Use sendInputMessage instead of sendChatMessage
                    disabled: chatState.busy,
                    ref: inputRef,
                });
            }

            return (
                <div className="flex rounded-md shadow-sm bg-white">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Type your question here..."
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyUp={(event) =>
                            event.key === "Enter" && sendInputMessage()
                        } // Use sendInputMessage
                        disabled={chatState.busy}
                        className={twMerge(
                            inputClassName,
                            chatState.busy ? "opacity-70" : ""
                        )}
                    />
                    <button
                        type="button"
                        onClick={sendInputMessage} // Use sendInputMessage (no arrow function)
                        disabled={chatState.busy}
                        className={twMerge(
                            sendButtonClassName,
                            chatState.busy ? "opacity-70" : ""
                        )}
                    >
                        <ArrowUp size={24} strokeWidth={2.75} />
                    </button>
                </div>
            );
        };

        // If a custom container renderer is provided, use it
        if (renderContainer) {
            return renderContainer({
                messages: messageElements,
                currentResponse: response,
                input: renderInputField(),
                followUps: followUpElements,
                chatState,
                isFirstBotMessage: firstBotMessageRef.current,
                chatContainerRef,
            });
        }

        // Default container
        return (
            <div className={containerClassName}>
                <div ref={chatContainerRef} className={chatContainerClassName}>
                    <div className={messageContainerClassName}>
                        {/* Privacy Policy banner */}
                        {showPrivacyBanner && (
                            <div
                                id="privacy-policy-banner"
                                className="mb-4 p-3 bg-neutral-100 text-xs text-neutral-600 relative"
                            >
                                <button
                                    className="absolute top-1 right-2 text-neutral-400 hover:text-neutral-600"
                                    onClick={() => {
                                        document.getElementById(
                                            "privacy-policy-banner"
                                        ).style.display = "none";
                                    }}
                                >
                                    ✕
                                </button>
                                <ReactMarkdown
                                    remarkPlugins={[gfm]}
                                    components={{
                                        a: CustomLink,
                                        small: CustomSmall,
                                    }}
                                    rehypePlugins={[rehypeSanitize]}
                                >
                                    {privacyPolicy || DEFAULT_PRIVACY_POLICY}
                                </ReactMarkdown>
                            </div>
                        )}
                        <div className="p-2">
                            {messageElements}
                            {response && (
                                <BotEntry
                                    entry={{ type: 2, message: response }}
                                    isFirstBotMessage={
                                        firstBotMessageRef.current
                                    }
                                    customDisclaimerText={customDisclaimerText}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className={inputContainerClassName}>
                    {followUps.length > 0 && (
                        <div className="follow-ups">{followUpElements}</div>
                    )}
                    {renderInputField()}
                </div>
            </div>
        );
    }
);

export default Chat;
